// contexts/ThemeContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define our theme types
type Theme = 'light' | 'dark';

// Define the context shape
interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

// Create the context with a default value
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Props for our provider component
interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

// Provider component that will wrap our app
export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'theme',
}: ThemeProviderProps) {
  // Initialize theme state from localStorage or system preference
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check for saved theme in localStorage
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem(storageKey) as Theme | null;
      
      if (savedTheme) {
        return savedTheme;
      }
      
      // Check for system preference
      if (window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
    }
    
    // Default to the provided defaultTheme
    return defaultTheme;
  });

  // Function to actually change the theme
  const setTheme = (newTheme: Theme) => {
    // Update state
    setThemeState(newTheme);
    
    // Save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, newTheme);
    }
  };

  // Effect to apply theme class to document
  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove both classes first
    root.classList.remove('light', 'dark');
    
    // Add the current theme class
    root.classList.add(theme);
    
    // Set data-theme attribute for components that use it
    root.setAttribute('data-theme', theme);
  }, [theme]);

  // Effect to listen for system preference changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      // Define media query change handler
      const handleChange = () => {
        // Only update if no theme is saved in localStorage
        if (!localStorage.getItem(storageKey)) {
          setThemeState(mediaQuery.matches ? 'dark' : 'light');
        }
      };
      
      // Add event listener with the handler
      mediaQuery.addEventListener('change', handleChange);
      
      // Clean up the listener
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    
    return undefined;
  }, [storageKey]);

  // Create a value object with the current theme and setTheme function
  const value = {
    theme,
    setTheme,
  };

  // Return the provider with our value
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use the theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}

export default ThemeContext;